import { Dispatch, SetStateAction, useEffect, useEffectEvent, useState } from "react";
import { Collapse } from "react-bootstrap";
import { IValidationFunction, IValidationOptionsInput } from "../utility";
import { getValidationOptions } from "../utility/getValidationOptions";
import { Control210 } from "./Control210";
import { Control211 } from "./Control211";

export interface ControlsGroupInputProps extends IValidationOptionsInput {
  computeValue?: (values: Record<string, string>) => string;
  dropdownOptions?: string[];
  forcePrefix?: string;
  hidden?: true | ((values: Record<string, string>) => boolean);
  initialValue?: string;
  inputLabel: string;
  inputName: string;
  multiple?: boolean;
  placeholder?: string;
  readonly?: true | ((values: Record<string, string>) => boolean);
  validCondition?: IValidationFunction;
  validationRequestUrl?: string;
}

export interface ControlsGroupProps {
  baseId: string;
  currentValues: Record<string, string>;
  inputs: ControlsGroupInputProps[];
  onFieldChange?: (allValues: Record<string, string>) => void;
  setCurrentValues: (values: Record<string, string>) => void;
  setTextValid?: Dispatch<SetStateAction<boolean[]>>;
  spellCheck?: boolean;
}

/**
 * Group of controls for use in forms.
 *
 * @param props - Props for the ControlsGroup component
 * @param props.baseId - Base ID for the controls
 * @param props.currentValues - Current values of the inputs
 * @param props.inputs - Array of input definitions
 * @param props.onFieldChange - Callback when a field changes
 * @param props.setCurrentValues - Function to update current values
 * @param props.setTextValid - Function to update text validity states
 * @param props.spellCheck - Whether to enable spell checking on text inputs
 * @returns React element containing the controls
 */
export const ControlsGroup: React.FC<ControlsGroupProps> = ({
  baseId,
  currentValues,
  inputs,
  onFieldChange,
  setCurrentValues,
  setTextValid,
  spellCheck = true,
}) => {
  // Recompute derived values when currentValues change
  const computedValues = Object.fromEntries(
    inputs
      .filter((inp) => inp.computeValue)
      .map((inp) => [inp.inputName, inp.computeValue!(currentValues)]),
  );

  // Compute hidden states for all inputs
  const hiddenStates = inputs.map((inp) =>
    typeof inp.hidden === "function"
      ? inp.hidden({ ...currentValues, ...computedValues })
      : (inp.hidden ?? false),
  );

  // Track previous hidden states to detect changes
  const [prevHiddenStates, setPrevHiddenStates] = useState<boolean[]>([]);

  const validateHidden = useEffectEvent(async (hiddenStates: boolean[]) => {
    for (const [ix, hidden] of hiddenStates.entries()) {
      // If an input is hidden, it is valid
      if (hidden) {
        setTextValid?.((prev) => [...prev.slice(0, ix), true, ...prev.slice(ix + 1)]);
      }
      // If an input has transitioned from hidden to visible, revalidate it.
      else if (!hidden && prevHiddenStates[ix] === true && inputs[ix].validCondition) {
        const mergedValues = { ...currentValues, ...computedValues };
        let valid = false;
        try {
          const ret = await inputs[ix].validCondition(
            inputs[ix].validationRequestUrl ?? "",
            {
              value: mergedValues[inputs[ix].inputName],
              options: getValidationOptions(inputs[ix], mergedValues),
            },
            new AbortController(),
          );
          valid = ret?.success ? ret.data.valid : false;
        } catch {
          valid = false;
        }
        setTextValid?.((prev) => [...prev.slice(0, ix), valid, ...prev.slice(ix + 1)]);
      }
    }
    setPrevHiddenStates(hiddenStates);
  });

  // Validate hidden states on mount and when they change
  useEffect(() => {
    if (!hiddenStates.every((val, ix) => val === prevHiddenStates[ix])) {
      validateHidden(hiddenStates);
    }
  }, [hiddenStates, prevHiddenStates]);

  return inputs.map((inp, ix) => {
    const mergedValues = { ...currentValues, ...computedValues };
    const sharedProps = {
      id: `${baseId}-${ix}`,
      label: inp.inputLabel,
      labelWidth: 4,
      value: mergedValues[inp.inputName],
      setValue: (ret: string) => {
        const newCurrentValues = { ...currentValues, [inp.inputName]: ret };
        const newComputedValues = Object.fromEntries(
          inputs
            .filter((i) => i.computeValue)
            .map((i) => [i.inputName, i.computeValue!(newCurrentValues)]),
        );
        setCurrentValues({ ...newCurrentValues, ...newComputedValues });
        onFieldChange?.({ ...newCurrentValues, ...newComputedValues });
      },
      disabled: typeof inp.readonly === "function" ? inp.readonly!(mergedValues) : inp.readonly,
      validationTargetOptions: getValidationOptions(inp, mergedValues),
    };

    return (
      <Collapse
        key={ix}
        in={!hiddenStates[ix]}
      >
        <div>
          {inp.dropdownOptions ? (
            <Control211
              key={inp.inputName}
              {...sharedProps}
              multiple={inp.multiple}
              options={[
                ...(!inp.multiple && !inp.dropdownOptions.includes(mergedValues[inp.inputName])
                  ? [{ label: "Select...", value: "" }]
                  : []),
                ...inp.dropdownOptions.map((opt) => ({ label: opt, value: opt })),
              ]}
              validCondition={inp.validCondition}
              validationReturn={(ret) =>
                setTextValid?.((prev) => [...prev.slice(0, ix), ret, ...prev.slice(ix + 1)])
              }
              validationRequestUrl={inp.validationRequestUrl}
            />
          ) : (
            <Control210
              key={inp.inputName}
              {...sharedProps}
              autoFocus={ix === 0}
              forcePrefix={inp.forcePrefix}
              placeholder={inp.placeholder}
              spellCheck={spellCheck}
              validCondition={inp.validCondition}
              validationReturn={(ret) =>
                setTextValid?.((prev) => [...prev.slice(0, ix), ret, ...prev.slice(ix + 1)])
              }
              validationRequestUrl={inp.validationRequestUrl}
              whiteSpaceFix={true}
            />
          )}
        </div>
      </Collapse>
    );
  });
};
